import React from 'react';
import {colors} from 'patient-ping-remedy/packages/theme';
import {SmallTypography, StandardTypography} from 'patient-ping-remedy/packages/typography';
import Badge from 'patient-ping-remedy/packages/badge';
import {differenceInCalendarDays} from 'date-fns';
import {TableData} from '@common/SortableTable';
import {TableLink} from '@common/TableLink.styles';
import DisplayHelpers from '@/helpers/display_helpers';
import {SearchParam} from '@/helpers/search_params';
import AssignedToTask from '@/components/pages/tasks/AssignedToTask';
import {Roster} from '@/api/dto/dto';
import {
  CareJourneyPriority,
  CareJourneyResponse, referralReviewStatusDisplayValues,
  taskStatusDisplayValues,
  TaskStepName,
  taskTypeDisplayValues,
} from '@/api/dto/care-journey';

export function formatLastViewed(lastViewedAt: string | null): string {
  if (!lastViewedAt) return '';
  const days = differenceInCalendarDays(new Date(), new Date(lastViewedAt));
  return days < 1 ? '<1 Day' : `${days} ${days === 1 ? 'Day ago' : 'Days ago'}`;
}

// Whole-day difference between the triggering event and today, or null when there is no
// valid trigger date. Compared at midnight so partial days do not round oddly.
export function getNumberOfDays(careJourney: CareJourneyResponse, today: Date = new Date()): number | null {
  if (!careJourney.triggeringEventDateTime) return null;
  const triggerDate = new Date(careJourney.triggeringEventDateTime);
  if (isNaN(triggerDate.getTime())) return null;
  const todayMidnight = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const triggerMidnight = new Date(triggerDate.getFullYear(), triggerDate.getMonth(), triggerDate.getDate());
  return Math.floor((todayMidnight.getTime() - triggerMidnight.getTime()) / (1000 * 60 * 60 * 24));
}

export interface CareJourneyRowOptions {
  // Resolves the displayed event-type label (wraps readableEncounterType + stateConfig).
  getEventType: (careJourney: CareJourneyResponse) => string;
  // Display names for task steps, from useTaskStepName. Optional: falls back to the raw step.
  taskStepDisplayValues?: Record<string, string>;
  // Resolves the roster for the AssignedToTask cell. Only needed when richAssignedTo is true.
  findRoster?: (rosterId?: number) => Roster | undefined;
  // Column toggles. Default (all true/undefined-as-true) is the full Care Journeys layout;
  // the pre-review table opts out of the columns it does not show.
  includeGroupOrg?: boolean;
  includeStatus?: boolean;
  includeTaskDate?: boolean;
  richName?: boolean;        // TableLink to the task detail page vs. plain text
  richAssignedTo?: boolean;  // AssignedToTask widget vs. plain text
  includeReferralReviewStatus?: boolean;  // ProviderReferralsTab only: ReferralReviewData.status
  today?: Date;
}

const taskDetailUrl = (careJourney: CareJourneyResponse): string =>
  `/tasks/details?${SearchParam.TASK_ID}=${careJourney.careJourneyInstanceId}`
  + (careJourney.rosterId !== undefined ? `&${SearchParam.ROSTER_ID}=${careJourney.rosterId}` : '');

export function buildCareJourneyRows(
  careJourneys: CareJourneyResponse[],
  options: CareJourneyRowOptions,
): TableData[] {
  const {
    getEventType,
    taskStepDisplayValues = {},
    findRoster,
    includeTaskDate = true,
    includeGroupOrg = true,
    includeStatus = true,
    richName = true,
    richAssignedTo = true,
    includeReferralReviewStatus = false,
    today = new Date(),
  } = options;

  return (careJourneys ?? []).map((careJourney): TableData => {
    const clientName = DisplayHelpers.formatName(
      careJourney.firstName, careJourney.middleName, careJourney.lastName,
    );
    const eventType = getEventType(careJourney);

    // The due date is a calendar day. Parsing it to local midnight from its date part keeps
    // the overdue comparison and the rendered day identical in every timezone (CARECO-4182).
    const dueDate = DisplayHelpers.parseCalendarDay(careJourney.dueDate);
    const todayMidnight = new Date(today);
    todayMidnight.setHours(0, 0, 0, 0);

    const assignedToValues = careJourney.userAssignment.users?.map((user) => user.id) ?? [];
    if (careJourney.userAssignment.assignedToAi) {
      assignedToValues.push('automated_agent');
    }

    const daysOpen = getNumberOfDays(careJourney, today);

    // Column order must match the header arrays in the consuming tabs. SortableTable
    // renders cells by the insertion order of these keys, so groupOrg/status are inserted
    // at their canonical positions (not appended) even when conditionally included.
    const row: TableData = {
      name: {
        tableHeader: 'Name', key: 'name', type: 'string',
        value: clientName,
        displayValue: richName
          ? <TableLink to={taskDetailUrl(careJourney)}>{clientName}</TableLink>
          : clientName,
        columnWidth: '15%',
      },
      dob: {
        tableHeader: 'Date of Birth', key: 'dob', type: 'date',
        value: careJourney.dateOfBirth,
        displayValue: DisplayHelpers.formatDateOfBirthWithAge(careJourney.dateOfBirth),
        columnWidth: '10%',
      },
      eventType: {
        tableHeader: 'Event type', key: 'eventType', type: 'string',
        value: [careJourney.triggeringEventDateTime, eventType].filter(Boolean).join(' '),
        displayValue: (
          <>
            <StandardTypography color={colors.primary1}>{eventType}</StandardTypography>
            <div>
              <SmallTypography>{DisplayHelpers.formatDate(careJourney.triggeringEventDateTime)}</SmallTypography>
            </div>
          </>
        ),
        columnWidth: '10%',
      },
      eventSetting: {
        tableHeader: 'Event Setting', key: 'eventSetting', type: 'string',
        value: careJourney.eventSetting ?? '-',
        displayValue: (
          <StandardTypography color={colors.primary1}>{careJourney.eventSetting ?? '-'}</StandardTypography>
        ),
        columnWidth: '9%',
      },
      daysOpen: {
        tableHeader: 'Days Open', key: 'daysOpen', type: 'number',
        value: daysOpen ?? '',
        displayValue: <StandardTypography color={colors.primary1}>{daysOpen ?? '-'}</StandardTypography>,
        columnWidth: '5%',
      },
      ...(includeGroupOrg ? {
        groupOrg: {
          tableHeader: 'Group / Org', key: 'groupOrg', type: 'string',
          value: [careJourney.groupName, careJourney.organizationName].filter(Boolean).join(' / '),
          displayValue: (
            <>
              <StandardTypography color={colors.primary1}>{careJourney.groupName}</StandardTypography>
              {careJourney.organizationName && (
                <div><SmallTypography>{careJourney.organizationName}</SmallTypography></div>
              )}
            </>
          ),
          columnWidth: '12%',
        },
      } : {}),
      ...(includeTaskDate ? {
        taskDate: {
          tableHeader: '', key: 'taskDate', type: 'date',
          value: careJourney.triggeringEventDateTime,
          displayValue: '',
          columnWidth: '0%',
        },
      }: {}),
      taskType: {
        tableHeader: 'Task type', key: 'taskType', type: 'string',
        value: careJourney.eventType,
        displayValue: careJourney.workflowName ?? taskTypeDisplayValues[careJourney.eventType],
        columnWidth: '20%',
      },
      ...(includeStatus ? {
        status: {
          tableHeader: 'Status', key: 'status', type: 'string',
          value: careJourney.status ? taskStatusDisplayValues[careJourney.status] ?? '' : '',
          displayValue: (
            <>
              <StandardTypography color={colors.primary1}>
                {careJourney.status ? taskStatusDisplayValues[careJourney.status] : ''}
              </StandardTypography>
              {dueDate !== null && (
                <div>
                  {dueDate >= todayMidnight
                    ? <SmallTypography>Due {DisplayHelpers.formatCalendarDate(careJourney.dueDate)}</SmallTypography>
                    : <SmallTypography color={colors.red5}>Overdue</SmallTypography>}
                </div>
              )}
            </>
          ),
          columnWidth: '10%',
        },
      } : {}),
      ...(includeReferralReviewStatus ? {
        referralReviewStatus: {
          tableHeader: 'Referral Review Status', key: 'referralReviewStatus', type: 'string',
          value: careJourney.referralReviewMetadata?.status
            ? referralReviewStatusDisplayValues[careJourney.referralReviewMetadata.status]
              ?? ''
            : '',
          displayValue: (
            <StandardTypography color={colors.primary1}>
              {careJourney.referralReviewMetadata?.status
                ? referralReviewStatusDisplayValues[careJourney.referralReviewMetadata.status]
                : ''}
            </StandardTypography>
          ),
          columnWidth: '10%',
        },
      } : {}),
      dueDate: {
        tableHeader: '', key: 'dueDate', type: 'date',
        value: DisplayHelpers.formatCalendarDate(careJourney.dueDate) || ' ',
        displayValue: '',
        columnWidth: '0%',
      },
      nextStep: {
        tableHeader: 'Next Step', key: 'nextStep', type: 'string',
        value: careJourney.nextStep ?? '',
        displayValue: (
          <>
            <StandardTypography color={colors.primary1}>
              {careJourney.nextStepLabel
                    ?? taskStepDisplayValues[careJourney.nextStep as TaskStepName]
                    ?? careJourney.nextStep}
            </StandardTypography>
          </>
        ),
        columnWidth: '10%',
      },
      priority: {
        tableHeader: 'Priority', key: 'priority', type: 'string',
        value: careJourney.priority ?? 'none',
        displayValue: careJourney.priority && (
          <Badge
            backgroundColor={careJourney.priority === CareJourneyPriority.URGENT ? colors.red1 : colors.gray1}
            textColor={careJourney.priority === CareJourneyPriority.URGENT ? colors.red5 : colors.charcoal}
          >
            {DisplayHelpers.humanize(careJourney.priority)}
          </Badge>
        ),
        columnWidth: '8%',
      },
      lastViewed: {
        tableHeader: 'Last Viewed', key: 'lastViewed', type: 'string',
        value: careJourney.lastViewedAt ?? '',
        displayValue: (
          <StandardTypography color={colors.primary1}>
            {formatLastViewed(careJourney.lastViewedAt)}
          </StandardTypography>
        ),
        columnWidth: '8%',
      },
      assignedTo: {
        tableHeader: 'Assigned to', key: 'assignedTo', type: 'custom',
        value: assignedToValues,
        displayValue: richAssignedTo
          ? (
            <AssignedToTask
              careJourney={careJourney}
              roster={findRoster?.(careJourney.rosterId)}
              showAiAssignment={careJourney.journeySource !== 'FLOWABLE'}
            />
          )
          : '',
        columnWidth: '12%',
      },
    };

    return row;
  });
}
